/*
 * SelahFamily — in-app ecosystem section mirroring selahslate.com's
 * "The Selah & Stone Legacy Holdings Family" panel.
 *
 * Styled in the Restful Night-Mode palette but with ROSE-GOLD accents
 * (not yellow gold) sourced from selahslate.com:
 *   – Primary rose      : #B76E79
 *   – Rose blush        : #E0BFB8
 *   – Sage (live badge) : #7A946C
 *
 * Three cards, matching what Selah Slate's landing page ships:
 *   1. Selah Slate™         — LIVE NOW (external links to stores/site)
 *   2. Selah Downloads™     — YOU'RE HERE (featuring Selah Rest™)
 *   3. Selah & Salt Home™   — COMING SOON (waiting list)
 *
 * Selah Rest is intentionally NOT rendered as a separate card — it is a
 * feature of Selah Downloads, and callouts to it live inside the
 * Downloads card's tagline.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Sparkles,
  Home as HomeIcon,
  Feather,
  ExternalLink,
  Mail,
  ArrowRight,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getPlatformTarget } from '../utils/platform';

const PAL = {
  // Restful night-mode base
  cardTop: '#1C1E21',
  cardBottom: '#17191B',
  hairline: 'rgba(253, 251, 247, 0.08)',
  alabaster: '#FDFBF7',
  fgSubtle: 'rgba(253, 251, 247, 0.72)',
  fgMuted: 'rgba(253, 251, 247, 0.52)',
  fgFaint: 'rgba(253, 251, 247, 0.35)',
  // Rose gold family (matches selahslate.com)
  rose: '#B76E79',
  roseDeep: '#A05863',
  roseBlush: '#E0BFB8',
  // Status colors
  sage: '#7A946C',
};

const BRANDS = [
  {
    id: 'slate',
    name: 'Selah Slate\u2122',
    tagline: 'Clear your credit. Reclaim your legacy.',
    body:
      'A free, faith-based companion that helps you draft FCRA credit-dispute letters, track bureau responses, and rebuild with Scripture-grounded encouragement. No fees, no ads, no selling your data \u2014 ever.',
    status: 'live',
    Icon: ShieldCheck,
    accent: PAL.rose,
    links: [
      // Website link — safe on every platform (owner-controlled domain).
      { label: 'Visit selahslate.com', href: 'https://selahslate.com', primary: true },
      // Store links — each is tagged so that on wrapped builds we only show
      // the link for the CURRENT platform's store. Apple 3.1.1 (Anti-Steering)
      // is strictest on paid apps: even though Selah Slate is FREE, we still
      // defensively hide competing-store links on iOS/Android/Windows wrapped
      // builds so App Review never sees "another way to install" language.
      {
        label: 'iOS',
        href: 'https://apps.apple.com/us/app/selah-slate/id6779376823',
        platform: 'ios',
      },
      {
        label: 'Android',
        href: 'https://play.google.com/store/apps/details?id=com.selahandstone.selahslate',
        platform: 'android',
      },
      {
        label: 'Windows',
        href: 'https://apps.microsoft.com/detail/9PJF6L5GH0TF',
        platform: 'windows',
      },
    ],
  },
  {
    id: 'downloads',
    name: 'Selah Downloads\u2122',
    tagline: 'featuring Selah Rest\u2122',
    body:
      'A restful, encrypted sanctuary for the spiritual downloads God gives you \u2014 voice or written \u2014 with AI-guided insights, Scripture, and prayer. Every capture ends in a 60-second Selah Rest breathing moment.',
    status: 'here',
    Icon: Feather,
    accent: PAL.roseBlush,
  },
  {
    id: 'home',
    name: 'Selah & Salt Home\u2122',
    tagline: 'A hearth for Scripture-rooted homemaking.',
    body:
      'Scripture-anchored meal planning, hospitality rhythms, and seasonal home reflections \u2014 carrying the same unhurried tone into the kitchen table and the family calendar. For the gatherers and the feeders.',
    status: 'coming',
    Icon: HomeIcon,
    accent: PAL.roseBlush,
    notify: {
      email: 'hello@selahandstonelegacy.com',
      note: 'Waiting list opens with the beta.',
    },
  },
];

function StatusBadge({ status }) {
  let label, color;
  if (status === 'live') {
    label = 'Live now';
    color = PAL.sage;
  } else if (status === 'here') {
    label = "You\u2019re here";
    color = PAL.rose;
  } else {
    label = 'Coming soon';
    color = PAL.roseDeep;
  }
  return (
    <span
      data-testid={`family-badge-${status}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] uppercase tracking-[0.22em] font-semibold"
      style={{
        color,
        background: `${color}18`,
        border: `1px solid ${color}44`,
      }}
    >
      <span
        aria-hidden
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: color, opacity: 0.9 }}
      />
      {label}
    </span>
  );
}

function BrandLinks({ links }) {
  if (!links || links.length === 0) return null;

  // Platform-aware filtering (Apple 3.1.1 defensive posture):
  //   • iOS build      → drop links tagged for 'android' and 'windows'
  //   • Android build  → drop 'ios' and 'windows'
  //   • Windows build  → drop 'ios' and 'android'
  //   • Web / desktop  → show all (user can be on any platform)
  // Links without a `platform` field (e.g. the primary website link) are
  // always shown regardless of build target.
  const target = getPlatformTarget();
  const visibleLinks = links.filter((link) => {
    if (!link.platform) return true; // untagged (website / other) always visible
    if (target === 'ios') return link.platform === 'ios';
    if (target === 'android') return link.platform === 'android';
    if (target === 'windows') return link.platform === 'windows';
    return true; // web / unknown → show every store link
  });

  if (visibleLinks.length === 0) return null;

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {visibleLinks.map((link) => (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`family-link-${link.label.toLowerCase().replace(/\W+/g, '-')}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-1"
          style={
            link.primary
              ? {
                  background: PAL.rose,
                  color: '#fff',
                  border: `1px solid ${PAL.rose}`,
                }
              : {
                  background: 'transparent',
                  color: PAL.roseBlush,
                  border: `1px solid rgba(224, 191, 184, 0.32)`,
                }
          }
        >
          {link.label}
          {link.primary && <ArrowRight size={11} />}
          {!link.primary && <ExternalLink size={10} style={{ opacity: 0.7 }} />}
        </a>
      ))}
    </div>
  );
}

function NotifyLine({ notify }) {
  if (!notify) return null;
  return (
    <div
      className="mt-5 pt-4 flex items-start gap-2"
      style={{ borderTop: `1px solid ${PAL.hairline}` }}
    >
      <Mail size={13} className="mt-0.5 flex-shrink-0" style={{ color: PAL.roseBlush, opacity: 0.7 }} />
      <p className="text-[11.5px] leading-relaxed" style={{ color: PAL.fgMuted }}>
        {notify.note}{' '}
        Contact{' '}
        <a
          href={`mailto:${notify.email}`}
          className="underline underline-offset-2 hover:opacity-100"
          style={{ color: PAL.roseBlush }}
        >
          {notify.email}
        </a>
        .
      </p>
    </div>
  );
}

function BrandCard({ brand, index }) {
  const { name, tagline, body, status, Icon, accent, links, notify } = brand;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      whileHover={{ y: -2 }}
      className="relative rounded-2xl overflow-hidden h-full flex flex-col"
      style={{
        background: `linear-gradient(180deg, ${PAL.cardTop} 0%, ${PAL.cardBottom} 100%)`,
        border: `1px solid ${PAL.hairline}`,
        boxShadow: '0 10px 30px -18px rgba(0,0,0,0.7), inset 0 1px 0 rgba(253,251,247,0.02)',
      }}
      data-testid={`selah-family-card-${brand.id}`}
    >
      {/* Rose-gold top accent line */}
      <div
        aria-hidden
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${PAL.rose} 30%, ${PAL.roseBlush} 70%, transparent 100%)`,
          opacity: 0.85,
        }}
      />

      <div className="p-6 flex flex-col flex-1">
        {/* Icon + status row */}
        <div className="flex items-center justify-between mb-5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: `${accent}18`,
              border: `1px solid ${accent}33`,
            }}
          >
            <Icon size={17} style={{ color: accent }} />
          </div>
          <StatusBadge status={status} />
        </div>

        {/* Name */}
        <h3
          className="text-[22px] leading-tight font-semibold mb-1.5"
          style={{
            color: PAL.alabaster,
            fontFamily: '"Playfair Display", serif',
            letterSpacing: '-0.005em',
          }}
        >
          {name}
        </h3>

        {/* Tagline */}
        <p
          className="text-[13px] italic mb-4"
          style={{
            color: PAL.roseBlush,
            fontFamily: '"Crimson Text", serif',
            letterSpacing: '0.005em',
          }}
        >
          {tagline}
        </p>

        {/* Body */}
        <p
          className="text-[13.5px] leading-relaxed"
          style={{ color: PAL.fgSubtle }}
        >
          {body}
        </p>

        {/* Links (Slate) OR notify (Home) OR nothing (Downloads-you-are-here) */}
        <div className="mt-auto">
          <BrandLinks links={links} />
          <NotifyLine notify={notify} />
        </div>
      </div>

      {/* Ambient rose glow at bottom-right */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-16 w-48 h-48 rounded-full"
        style={{
          background: `radial-gradient(circle, rgba(183,110,121,0.18) 0%, transparent 65%)`,
          filter: 'blur(20px)',
        }}
      />
    </motion.article>
  );
}

export default function SelahFamily({ className = '' }) {
  const { scopeActive, effective } = useTheme();
  // When the ThemeScope is inactive (public pages), --sd-* vars fall back to the
  // day palette. On authenticated pages, day/night is driven by user preference.
  // In day mode we swap section-level text (which sits on the transparent page
  // bg) to darker plum + deeper rose so it stays readable on cream.
  const isDay = scopeActive ? effective === 'light' : true;
  const SECTION = isDay
    ? {
        heading: '#1C1C1C',
        subtle: 'rgba(28, 28, 28, 0.78)',
        muted: 'rgba(28, 28, 28, 0.62)',
        faint: 'rgba(28, 28, 28, 0.55)',
        accent: '#8B5A64', // deeper rose for cream bg
        accentSoft: 'rgba(139, 90, 100, 0.10)',
        hairline: 'rgba(28, 28, 28, 0.12)',
      }
    : {
        heading: PAL.alabaster,
        subtle: PAL.fgSubtle,
        muted: PAL.fgMuted,
        faint: PAL.fgFaint,
        accent: PAL.roseBlush,
        accentSoft: 'rgba(183, 110, 121, 0.10)',
        hairline: PAL.hairline,
      };

  return (
    <section
      data-testid="selah-family-section"
      className={className}
    >
      {/* Hairline rose ornament label */}
      <div className="flex items-center gap-4 mb-5">
        <div
          className="flex-1 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${SECTION.hairline}, transparent)`,
          }}
        />
        <span
          className="text-[10.5px] uppercase tracking-[0.42em]"
          style={{ color: SECTION.accent, fontFamily: '"Playfair Display", serif' }}
        >
          &middot; more is coming &middot;
        </span>
        <div
          className="flex-1 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${SECTION.hairline}, transparent)`,
          }}
        />
      </div>

      {/* Section heading */}
      <div className="flex items-start gap-3 mb-8">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: SECTION.accentSoft,
            border: `1px solid ${SECTION.hairline}`,
          }}
        >
          <Sparkles size={17} style={{ color: SECTION.accent }} />
        </div>
        <div>
          <h2
            className="text-[22px] leading-tight font-semibold"
            style={{
              color: SECTION.heading,
              fontFamily: '"Playfair Display", serif',
            }}
          >
            The Selah &amp; Stone Legacy Holdings Family
          </h2>
          <p
            className="text-[13px] leading-relaxed mt-1 max-w-xl"
            style={{ color: SECTION.subtle }}
          >
            Selah Downloads is one of several legacy-mission tools we&rsquo;re
            building for families like yours. Each one is Scripture-anchored,
            privacy-first, and made to outlive us.
          </p>
        </div>
      </div>

      {/* Three brand cards */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-5"
        data-testid="selah-family-grid"
      >
        {BRANDS.map((b, i) => (
          <BrandCard key={b.id} brand={b} index={i} />
        ))}
      </div>
    </section>
  );
}
